import { useRef, useState, type PointerEvent } from 'react'
import styled from '@emotion/styled'
import { zIndex } from 'src/constants/zIndex'

interface Props {
    onComplete: (lasso: [number, number][], size: [number, number]) => void
}

// Transparent layer that captures a freehand drawing, in pixels relative to itself
export function LassoOverlay({ onComplete }: Props) {
    const [points, setPoints] = useState<[number, number][]>([])
    const drawing = useRef(false)

    const toLocalPoint = (e: PointerEvent<HTMLDivElement>): [number, number] => {
        const rect = e.currentTarget.getBoundingClientRect()

        return [e.clientX - rect.left, e.clientY - rect.top]
    }

    const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
        e.stopPropagation()
        e.currentTarget.setPointerCapture?.(e.pointerId)
        drawing.current = true
        setPoints([toLocalPoint(e)])
    }

    const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
        if (!drawing.current) {
            return
        }
        const point = toLocalPoint(e)

        setPoints(prev => [...prev, point])
    }

    const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
        if (!drawing.current) {
            return
        }
        const { width, height } = e.currentTarget.getBoundingClientRect()

        drawing.current = false
        onComplete(points, [width, height])
        setPoints([])
    }

    return (
        <Overlay
            data-testid='vmap-lasso-overlay'
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
        >
            <svg width='100%' height='100%'>
                {points.length > 1 && (
                    <polygon
                        data-testid='vmap-lasso-polygon'
                        points={points.map(p => p.join(',')).join(' ')}
                        fill='rgba(242, 101, 103, 0.2)'
                        stroke='#f26567'
                        strokeWidth={2}
                        strokeDasharray='6 4'
                    />
                )}
            </svg>
        </Overlay>
    )
}

const Overlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: ${zIndex.low};
    cursor: crosshair;
    touch-action: none;

    svg {
        display: block;
        pointer-events: none;
    }
`
